import * as RadixAccordion from '@radix-ui/react-accordion';
import { CaretDownIcon } from '@radix-ui/react-icons';
import { Button, Dialog } from '@scholastic/mockingjay';
import { type ReactNode } from 'react';
import GoogleImportErrorIcon from '@roster/assets/icons/google-import-error.svg';
import GoogleImportSuccessIcon from '@roster/assets/icons/google-import-success.svg';
import {
  groupImportGoogleClassesStudentErrors,
  type ImportGoogleClassesApiResponse,
} from '../../hooks/api/use-import-google-classes-api/use-import-google-classes-api';

const hiddenTrigger = (
  <button type="button" className="sr-only" tabIndex={-1} aria-hidden>
    Open Google classes import issues dialog
  </button>
);

const BUTTON_PADDING_CLASS_NAME = 'py-2.5';

interface ImportGoogleClassesIssuesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: ImportGoogleClassesApiResponse | null;
  title?: string;
  action?: 'import' | 'sync';
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

function getStudentChangeSummary(
  studentsAddedCount = 0,
  studentsRemovedCount = 0
) {
  const summaryParts = [
    studentsAddedCount > 0
      ? `${studentsAddedCount} ${pluralize(
          studentsAddedCount,
          'student'
        )} added`
      : null,
    studentsRemovedCount > 0
      ? `${studentsRemovedCount} ${pluralize(
          studentsRemovedCount,
          'student'
        )} removed`
      : null,
  ].filter(Boolean);

  return summaryParts.join(', ');
}

function SectionBody({
  children,
  footer,
  scrollable = false,
}: {
  children: ReactNode;
  footer?: ReactNode;
  scrollable?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className={`rounded-lg border border-[#D4D4D4] bg-white px-3 py-2 ${
          scrollable ? 'max-h-[220px] overflow-y-auto' : ''
        }`}
      >
        {children}
      </div>
      {footer}
    </div>
  );
}

const ACCORDION_TRIGGER_CLASS_NAME =
  'group flex items-center gap-3 rounded-[4px] bg-white p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#191918] focus-visible:ring-offset-2';

const ACCORDION_TRIGGER_LABEL_CLASS_NAME =
  'flex items-center gap-1 text-base font-bold leading-10 text-[#313131]';

const ACCORDION_CARET_CLASS_NAME =
  'size-4 shrink-0 -rotate-90 text-[#191918] transition-transform duration-200 group-data-[state=open]:rotate-0';

function ImportAccordionSection({
  value,
  icon,
  label,
  children,
  footer,
  scrollable,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  children: ReactNode;
  footer?: ReactNode;
  scrollable?: boolean;
}) {
  return (
    <RadixAccordion.Item value={value}>
      <RadixAccordion.Header className="m-0">
        <RadixAccordion.Trigger className={ACCORDION_TRIGGER_CLASS_NAME}>
          <CaretDownIcon aria-hidden className={ACCORDION_CARET_CLASS_NAME} />
          <span className={ACCORDION_TRIGGER_LABEL_CLASS_NAME}>
            {icon}
            {label}
          </span>
        </RadixAccordion.Trigger>
      </RadixAccordion.Header>
      <RadixAccordion.Content className="pt-1 text-[#191918]">
        <SectionBody footer={footer} scrollable={scrollable}>
          {children}
        </SectionBody>
      </RadixAccordion.Content>
    </RadixAccordion.Item>
  );
}

export function ImportGoogleClassesIssuesDialog({
  open,
  onOpenChange,
  result,
  title = 'Import issues',
  action = 'import',
}: ImportGoogleClassesIssuesDialogProps) {
  const successVerb = action === 'sync' ? 'synced' : 'imported';
  const failedVerb = action === 'sync' ? 'failed to sync' : 'failed to import';
  const successHeading =
    action === 'sync' ? 'Synced classes:' : 'Imported classes:';
  const syncedClasses = result?.success?.classesSynced ?? [];
  const classErrors = result?.errors?.classErrors ?? [];
  const studentErrors = result?.errors?.studentErrors ?? [];
  const groupedStudentErrors =
    groupImportGoogleClassesStudentErrors(studentErrors);
  const defaultOpenItems = [
    classErrors.length > 0 ? 'class-errors' : null,
    studentErrors.length > 0 ? 'student-errors' : null,
  ].filter(Boolean) as string[];

  if (!result || defaultOpenItems.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={hiddenTrigger}
      title={title}
      description=""
    >
      <div className="-mt-6 w-[calc(100vw-3rem)] min-w-[350px] max-w-[700px]">
        <RadixAccordion.Root
          type="multiple"
          className="flex flex-col gap-6"
          defaultValue={defaultOpenItems}
        >
          {syncedClasses.length > 0 ? (
            <ImportAccordionSection
              value="success"
              icon={
                <GoogleImportSuccessIcon className="size-6 shrink-0 text-[#007F17]" />
              }
              label={`${syncedClasses.length} ${pluralize(
                syncedClasses.length,
                'class',
                'classes'
              )} ${successVerb}`}
            >
              <div className="flex flex-col gap-2">
                <p className="text-base font-medium leading-6 text-[#4D4D4D]">
                  {successHeading}
                </p>
                <div className="flex flex-col gap-2">
                  {syncedClasses.map((syncedClass) => {
                    const studentChangeSummary = getStudentChangeSummary(
                      syncedClass.studentsAddedCount,
                      syncedClass.studentsRemovedCount
                    );

                    return (
                      <div
                        key={syncedClass.className}
                        className="flex flex-col gap-1"
                      >
                        <p className="text-base font-medium leading-6 text-[#191918]">
                          {syncedClass.className}
                        </p>
                        {studentChangeSummary ? (
                          <p className="text-sm font-medium leading-6 text-[#4D4D4D]">
                            {studentChangeSummary}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </ImportAccordionSection>
          ) : null}

          {classErrors.length > 0 ? (
            <ImportAccordionSection
              value="class-errors"
              icon={<GoogleImportErrorIcon className="size-6 shrink-0" />}
              label={`${classErrors.length} ${pluralize(
                classErrors.length,
                'class',
                'classes'
              )} ${failedVerb}`}
              footer={
                <p className="flex items-start gap-1 text-sm font-medium leading-6 text-[#DB0C0C]">
                  <GoogleImportErrorIcon className="mt-1 size-4 shrink-0" />
                  Check these class names in Google Classroom and try again.
                </p>
              }
            >
              <div className="flex flex-col gap-2">
                <p className="text-base font-medium leading-6 text-[#4D4D4D]">
                  Failed classes:
                </p>
                <div className="flex flex-col gap-2">
                  {classErrors.map((classError) => (
                    <p
                      key={classError.className}
                      className="text-base font-medium leading-6 text-[#191918]"
                    >
                      {classError.className}
                    </p>
                  ))}
                </div>
              </div>
            </ImportAccordionSection>
          ) : null}

          {studentErrors.length > 0 ? (
            <ImportAccordionSection
              value="student-errors"
              icon={<GoogleImportErrorIcon className="size-6 shrink-0" />}
              label={`${studentErrors.length} ${pluralize(
                studentErrors.length,
                'student'
              )} ${failedVerb}`}
              scrollable
              footer={
                <p className="flex items-start gap-1 text-sm font-medium leading-6 text-[#DB0C0C]">
                  <GoogleImportErrorIcon className="mt-1 size-4 shrink-0" />
                  Check the student names in Google Classroom and try again.
                </p>
              }
            >
              <div className="flex flex-col gap-2">
                <p className="text-base font-medium leading-6 text-[#4D4D4D]">
                  Failed students:
                </p>
                <div className="flex flex-col gap-4">
                  {groupedStudentErrors.map((studentErrorGroup) => (
                    <div
                      key={studentErrorGroup.className}
                      className="flex flex-col gap-1"
                    >
                      <p className="text-xs font-medium leading-[1.5] text-[#6D6D6D]">
                        {studentErrorGroup.className}
                      </p>
                      <div className="flex flex-col gap-2">
                        {studentErrorGroup.students.map((studentError) => (
                          <p
                            key={`${studentErrorGroup.className}-${studentError.studentName}`}
                            className="text-base font-medium leading-6 text-[#191918]"
                          >
                            {studentError.studentName}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ImportAccordionSection>
          ) : null}
        </RadixAccordion.Root>

        <div className="mt-8 flex justify-end">
          <Button
            variant="primary"
            className={BUTTON_PADDING_CLASS_NAME}
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export type { ImportGoogleClassesIssuesDialogProps };
